import { useEffect, useState } from 'react';
import { Users, Eye, MessageSquare, MousePointer, TrendingUp, Globe } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import AdminSidebar from '../components/AdminSidebar';
import api from '../lib/api';

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/admin/dashboard');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#06b6d4', '#8b5cf6', '#10b981'];

  const StatCard = ({ icon: Icon, title, value, subtitle, color = 'cyan' }) => (
    <div className="stat-card p-6 rounded-xl bg-slate-900/50 border border-white/10" data-testid={`stat-${title.toLowerCase().replace(' ', '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-400 mb-1">{title}</p>
          <p className="text-3xl font-heading font-bold text-white">{value}</p>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg bg-${color}-500/10`}>
          <Icon className={`h-6 w-6 text-${color}-400`} />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex">
        <AdminSidebar />
        <main className="flex-1 ml-64 p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 rounded-xl skeleton" />
            ))}
          </div>
          <div className="h-80 rounded-xl skeleton" />
        </main>
      </div>
    );
  }

  const inquiryPieData = analytics ? [
    { name: 'Info Requests', value: analytics.inquiries_by_type.info },
    { name: 'Buy Inquiries', value: analytics.inquiries_by_type.buy },
    { name: 'Investment', value: analytics.inquiries_by_type.invest },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="admin-dashboard">
      <AdminSidebar />
      
      <main className="flex-1 ml-64 p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-white mb-2">Dashboard</h1>
          <p className="text-slate-400">Overview of your platform analytics</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={Eye}
            title="Page Views"
            value={analytics?.total_page_views || 0}
            subtitle="Total page views"
            color="cyan"
          />
          <StatCard
            icon={Users}
            title="Unique Visitors"
            value={analytics?.unique_visitors || 0}
            subtitle="Distinct visitors"
            color="violet"
          />
          <StatCard
            icon={MessageSquare}
            title="Inquiries"
            value={analytics?.total_inquiries || 0}
            subtitle="All time"
            color="emerald"
          />
          <StatCard
            icon={MousePointer}
            title="Button Clicks"
            value={(analytics?.click_stats?.info || 0) + (analytics?.click_stats?.buy || 0) + (analytics?.click_stats?.invest || 0)}
            subtitle="Action buttons"
            color="amber"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Views Chart */}
          <div className="lg:col-span-2 p-6 rounded-xl bg-slate-900/50 border border-white/10" data-testid="views-chart">
            <h3 className="font-heading font-semibold text-white mb-4">Views (Last 7 Days)</h3>
            {analytics?.views_by_day?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={analytics.views_by_day}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#64748b" 
                    tick={{ fill: '#94a3b8', fontSize: 12 }}
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#0f172a', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="views" 
                    stroke="#06b6d4" 
                    strokeWidth={2}
                    dot={{ fill: '#06b6d4', r: 4 }}
                    activeDot={{ r: 6, stroke: '#06b6d4', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-500">
                No view data yet
              </div>
            )}
          </div>

          {/* Inquiry Types */}
          <div className="p-6 rounded-xl bg-slate-900/50 border border-white/10" data-testid="inquiry-pie-chart">
            <h3 className="font-heading font-semibold text-white mb-4">Inquiries by Type</h3>
            {inquiryPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={inquiryPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {inquiryPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      background: '#0f172a', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-500">
                No inquiry data yet
              </div>
            )}
            {inquiryPieData.length > 0 && (
              <div className="flex flex-wrap justify-center gap-4 mt-4">
                {inquiryPieData.map((entry, index) => (
                  <div key={entry.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ background: COLORS[index] }} />
                    <span className="text-xs text-slate-400">{entry.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Ideas */}
          <div className="p-6 rounded-xl bg-slate-900/50 border border-white/10" data-testid="top-ideas">
            <h3 className="font-heading font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-cyan-400" />
              Top Ideas by Views
            </h3>
            {analytics?.top_ideas?.length > 0 ? (
              <div className="space-y-3">
                {analytics.top_ideas.map((idea, idx) => (
                  <div key={idea.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-950/50 border border-white/5">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-mono text-slate-500">#{idx + 1}</span>
                      <span className="text-white truncate max-w-[200px]">{idea.title}</span>
                    </div>
                    <span className="text-cyan-400 font-mono text-sm">{idea.views} views</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No ideas yet</p>
            )}
          </div>

          {/* Recent Visitors */}
          <div className="p-6 rounded-xl bg-slate-900/50 border border-white/10" data-testid="recent-visitors">
            <h3 className="font-heading font-semibold text-white mb-4 flex items-center gap-2">
              <Globe className="h-5 w-5 text-violet-400" />
              Recent Visitors
            </h3>
            {analytics?.recent_visitors?.length > 0 ? (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {analytics.recent_visitors.map((visitor) => (
                  <div key={visitor.id || visitor.ip} className="p-3 rounded-lg bg-slate-950/50 border border-white/5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white text-sm font-mono">
                        {visitor.name || visitor.ip}
                      </span>
                      <span className="text-xs text-slate-500">
                        {visitor.visit_count} visits
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      {visitor.email && <span>{visitor.email}</span>}
                      {visitor.location?.city && visitor.location.city !== 'Unknown' && (
                        <span>• {visitor.location.city}, {visitor.location.country}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No visitors yet</p>
            )}
          </div>
        </div>

        {/* Click Stats */}
        <div className="mt-6 p-6 rounded-xl bg-slate-900/50 border border-white/10" data-testid="click-stats">
          <h3 className="font-heading font-semibold text-white mb-4">Button Click Stats</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
              <p className="text-sm text-cyan-400 mb-1">Get Info Clicks</p>
              <p className="text-2xl font-bold text-white">{analytics?.click_stats?.info || 0}</p>
            </div>
            <div className="p-4 rounded-lg bg-violet-500/10 border border-violet-500/20">
              <p className="text-sm text-violet-400 mb-1">Buy Clicks</p>
              <p className="text-2xl font-bold text-white">{analytics?.click_stats?.buy || 0}</p>
            </div>
            <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <p className="text-sm text-emerald-400 mb-1">Invest Clicks</p>
              <p className="text-2xl font-bold text-white">{analytics?.click_stats?.invest || 0}</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
